import React from "react";
import { Link } from "react-router-dom";
import {
 ArrowUpRight, Snowflake, Wind, Thermometer, Wrench, Factory, ShieldCheck,
 Truck, ChevronDown, Building2, Hospital, Utensils, Fish, Warehouse, Zap
} from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import RealisationsGallery from "@/components/RealisationsGallery";
import { useT } from "@/i18n/helpers";
import Seo from "@/components/Seo";

// PAGE PILIER B2B. Réfrigération & climatisation industrielle
// Contenu 100% trilingue FR / EN / PT (P1 · session 9).

const Section = ({ children, className = "", id }) => (
 <section id={id} className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">{children}</div>
 </section>
);
const Overline = ({ children }) => (<div className="label-overline text-nanda-muted mb-4">{children}</div>);

export default function RefrigerationClimatisation() {
 const tt = useT();

 const SERVICES = [
 { i: Snowflake,
 t: tt("Chambres froides", "Cold rooms", "Câmaras frias"),
 d: tt("Étude, dimensionnement, fourniture et installation de chambres froides positives et négatives pour l'agroalimentaire, la pêche, la restauration et la santé.",
 "Design, sizing, supply and installation of positive and negative cold rooms for food industry, fisheries, catering and healthcare.",
 "Estudo, dimensionamento, fornecimento e instalação de câmaras frias positivas e negativas para agro-alimentar, pesca, restauração e saúde.") },
 { i: Thermometer,
 t: tt("Réfrigération commerciale", "Commercial refrigeration", "Refrigeração comercial"),
 d: tt("Vitrines, meubles positifs et négatifs, armoires froides pour supermarchés, boucheries, poissonneries et boulangeries.",
 "Display cabinets, positive and negative units, cold cabinets for supermarkets, butchers, fishmongers and bakeries.",
 "Vitrinas, unidades positivas e negativas, armários frios para supermercados, talhos, peixarias e padarias.") },
 { i: Wind,
 t: tt("Climatisation split & multisplit", "Split & multisplit air conditioning", "Climatização split e multisplit"),
 d: tt("Solutions de confort thermique pour bureaux, commerces, logements, hôtels et administrations.",
 "Thermal comfort solutions for offices, retail, homes, hotels and public buildings.",
 "Soluções de conforto térmico para escritórios, comércio, habitações, hotéis e administrações.") },
 { i: Building2,
 t: tt("Climatisation centralisée & VRV", "Centralised AC & VRV systems", "Climatização centralizada e VRV"),
 d: tt("Systèmes multi-zones pour bâtiments tertiaires, hôtels et bâtiments à forte densité.",
 "Multi-zone systems for tertiary buildings, hotels and high-density facilities.",
 "Sistemas multi-zonas para edifícios terciários, hotéis e edifícios de alta densidade.") },
 { i: Factory,
 t: tt("Chaîne du froid industrielle", "Industrial cold chain", "Cadeia de frio industrial"),
 d: tt("Groupes de production frigorifique, tunnels, entrepôts et process pour l'industrie alimentaire.",
 "Refrigeration production units, tunnels, warehouses and process solutions for the food industry.",
 "Grupos de produção frigorífica, túneis, entrepostos e processos para a indústria alimentar.") },
 { i: Wrench,
 t: tt("Maintenance & SAV", "Maintenance & after-sales", "Manutenção e SAV"),
 d: tt("Contrats d'entretien préventif, dépannage, remplacement de composants et suivi de performance énergétique.",
 "Preventive maintenance contracts, troubleshooting, component replacement and energy performance monitoring.",
 "Contratos de manutenção preventiva, resolução de avarias, substituição de componentes e acompanhamento de desempenho energético.") },
 ];

 const SECTEURS = [
 { i: Utensils,
 t: tt("Restauration & hôtellerie", "Catering & hospitality", "Restauração e hotelaria"),
 d: tt("Chambres froides, backs de cuisine, chaîne du froid pour hôtels, restaurants et traiteurs.",
 "Cold rooms, kitchen back-of-house, cold chain for hotels, restaurants and caterers.",
 "Câmaras frias, back-office de cozinha, cadeia de frio para hotéis, restaurantes e caterings.") },
 { i: Fish,
 t: tt("Pêche & agroalimentaire", "Fisheries & food industry", "Pesca e agro-alimentar"),
 d: tt("Chambres de stockage, tunnels de surgélation et process de transformation.",
 "Storage rooms, blast-freezing tunnels and processing solutions.",
 "Câmaras de armazenamento, túneis de ultracongelação e processos de transformação.") },
 { i: Hospital,
 t: tt("Santé & pharmacie", "Health & pharmaceuticals", "Saúde e farmácia"),
 d: tt("Chaîne du froid médicale, chambres froides vaccinales, laboratoires.",
 "Medical cold chain, vaccine cold rooms, laboratories.",
 "Cadeia de frio médica, câmaras frias vacinais, laboratórios.") },
 { i: Warehouse,
 t: tt("Logistique & distribution", "Logistics & distribution", "Logística e distribuição"),
 d: tt("Entrepôts frigorifiques, plateformes multi-températures, réseaux de distribution alimentaire.",
 "Refrigerated warehouses, multi-temperature platforms, food distribution networks.",
 "Entrepostos frigoríficos, plataformas multi-temperatura, redes de distribuição alimentar.") },
 { i: Building2,
 t: tt("Tertiaire & administrations", "Tertiary & public sector", "Terciário e administrações"),
 d: tt("Climatisation confort pour bureaux, banques, administrations publiques et sièges d'entreprises.",
 "Comfort air conditioning for offices, banks, public administrations and corporate headquarters.",
 "Climatização de conforto para escritórios, bancos, administrações públicas e sedes empresariais.") },
 { i: Factory,
 t: tt("Industrie", "Industry", "Indústria"),
 d: tt("Refroidissement de process, groupes d'eau glacée, salles techniques et data centers.",
 "Process cooling, chilled water units, technical rooms and data centres.",
 "Refrigeração de processo, grupos de água gelada, salas técnicas e data centres.") },
 ];

 const METHODE = [
 { n: "01", t: tt("Écoute du besoin", "Listening to your needs", "Escuta da necessidade"),
 d: tt("Nous partons de votre activité, de vos volumes, de vos contraintes de température et d'usage.",
 "We start from your activity, your volumes and your temperature and usage constraints.",
 "Partimos da sua actividade, dos seus volumes, das suas restrições de temperatura e de uso.") },
 { n: "02", t: tt("Étude & dimensionnement", "Study & sizing", "Estudo e dimensionamento"),
 d: tt("Bilan thermique, choix des équipements, cheminement des réseaux, optimisation énergétique.",
 "Thermal balance, equipment selection, piping layout, energy optimisation.",
 "Balanço térmico, escolha dos equipamentos, encaminhamento das redes, optimização energética.") },
 { n: "03", t: tt("Devis & planification", "Quote & planning", "Orçamento e planeamento"),
 d: tt("Chiffrage détaillé, planning d'intervention, coordination avec les autres corps de métier.",
 "Detailed pricing, intervention schedule, coordination with other trades.",
 "Orçamento detalhado, planeamento de intervenção, coordenação com os outros ofícios.") },
 { n: "04", t: tt("Fourniture & installation", "Supply & installation", "Fornecimento e instalação"),
 d: tt("Livraison, montage, brasure, mise sous vide, charge en fluide, essais de fonctionnement.",
 "Delivery, assembly, brazing, vacuum, refrigerant charging, operational testing.",
 "Entrega, montagem, brasagem, vácuo, carga de fluido, ensaios de funcionamento.") },
 { n: "05", t: tt("Mise en service", "Commissioning", "Colocação em serviço"),
 d: tt("Réglages, vérification des sécurités, formation des utilisateurs, remise des documents techniques.",
 "Settings, safety checks, user training, handover of technical documentation.",
 "Regulações, verificação das seguranças, formação dos utilizadores, entrega da documentação técnica.") },
 { n: "06", t: tt("Maintenance & suivi", "Maintenance & follow-up", "Manutenção e acompanhamento"),
 d: tt("Contrats préventifs, contrôles périodiques, suivi de performance et dépannage rapide.",
 "Preventive contracts, periodic checks, performance monitoring and quick troubleshooting.",
 "Contratos preventivos, controlos periódicos, acompanhamento de desempenho e resolução rápida de avarias.") },
 ];

 const ATOUTS = [
 { i: ShieldCheck, t: tt("Sécurité & normes", "Safety & standards", "Segurança e normas"),
 d: tt("Interventions conformes aux règles de sécurité, EPI, protocoles d'accès aux fluides frigorigènes.",
 "Interventions compliant with safety rules, PPE and refrigerant handling protocols.",
 "Intervenções conformes às regras de segurança, EPI, protocolos de acesso aos fluidos frigorígenos.") },
 { i: Truck, t: tt("Logistique intégrée", "Integrated logistics", "Logística integrada"),
 d: tt("Approvisionnement des équipements, réception, stockage et déploiement sur site.",
 "Equipment sourcing, reception, storage and on-site deployment.",
 "Abastecimento dos equipamentos, recepção, armazenamento e implantação em obra.") },
 { i: Wrench, t: tt("SAV local durable", "Sustainable local after-sales", "SAV local duradouro"),
 d: tt("Maintenance de proximité, pièces disponibles et techniciens formés.",
 "Local maintenance, available spare parts and trained technicians.",
 "Manutenção de proximidade, peças disponíveis e técnicos formados.") },
 { i: Zap, t: tt("Efficacité énergétique", "Energy efficiency", "Eficiência energética"),
 d: tt("Choix d'équipements performants et adaptés au climat ouest-africain.",
 "Selection of high-performance equipment suited to the West African climate.",
 "Escolha de equipamentos de alto desempenho e adaptados ao clima oeste-africano.") },
 ];

 return (
 <div data-testid="page-refrigeration-climatisation">
 <Seo />

 {/* HERO */}
 <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden bg-nanda-ink">
 <img
 src="/photos/realisations/refrigeration-02-chambre-froide-evaporateurs.webp"
 alt={tt(
 "Vue intérieure d'une chambre froide industrielle installée par NANDA SÁ ENERGIA PRO en Afrique de l'Ouest",
 "Interior view of an industrial cold room installed by NANDA SÁ ENERGIA PRO in West Africa",
 "Vista interior de uma câmara fria industrial instalada pela NANDA SÁ ENERGIA PRO na África Ocidental"
 )}
 className="absolute inset-0 h-full w-full object-cover opacity-70"
 loading="eager"
 />
 <div className="absolute inset-0 bg-gradient-to-b from-nanda-ink/50 via-nanda-ink/30 to-nanda-ink/80" />
 <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-24 md:pb-32">
 <FadeUp>
 <div className="label-overline text-white/85 mb-6">{tt("NANDA SÁ ENERGIA PRO · Métier historique", "NANDA SÁ ENERGIA PRO · Core expertise", "NANDA SÁ ENERGIA PRO · Ofício histórico")}</div>
 <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
 {tt("Réfrigération &", "Refrigeration &", "Refrigeração e")}<br />
 <span className="text-nanda-gold italic">{tt("climatisation industrielle.", "industrial air conditioning.", "climatização industrial.")}</span>
 </h1>
 <p className="mt-8 max-w-2xl text-white/85 text-lg md:text-xl leading-relaxed">
 {tt(
 "Étude, fourniture, installation et maintenance d'équipements frigorifiques et de climatisation professionnels au Sénégal, en Guinée-Bissau et en Afrique de l'Ouest.",
 "Design, supply, installation and maintenance of professional refrigeration and air-conditioning equipment in Senegal, Guinea-Bissau and West Africa.",
 "Estudo, fornecimento, instalação e manutenção de equipamentos frigoríficos e de climatização profissionais no Senegal, na Guiné-Bissau e na África Ocidental."
 )}
 </p>
 <div className="mt-12 flex flex-wrap gap-4">
 <Link to="/devis?category=refrigeration-industrielle" className="inline-flex items-center gap-3 bg-white text-nanda-ink px-8 py-4 label-overline hover:bg-nanda-gold hover:text-white transition-colors duration-500" data-testid="refri-cta-devis">
 {tt("Demander un devis", "Request a quote", "Solicitar orçamento")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <a href="#realisations" className="inline-flex items-center gap-3 border border-white/50 text-white px-8 py-4 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="refri-cta-realisations">
 {tt("Voir nos réalisations", "See our projects", "Ver as nossas realizações")} <ChevronDown size={16} strokeWidth={1.5} />
 </a>
 </div>
 </FadeUp>
 </div>
 </section>

 {/* PRÉSENTATION */}
 <Section>
 <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
 <FadeUp className="md:col-span-5">
 <Overline>{tt("Notre expertise", "Our expertise", "A nossa especialidade")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Le froid, c'est notre métier de fond.", "Refrigeration is our foundational trade.", "O frio é o nosso ofício de fundo.")}
 </h2>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 text-nanda-inkSoft text-[17px] leading-relaxed space-y-5">
 <p>{tt(
 "La réfrigération et la climatisation professionnelle sont l'un des cœurs de métier historiques de NANDA SÁ ENERGIA PRO. Nous concevons, installons et maintenons des équipements pour l'agroalimentaire, la pêche, la santé, la logistique, l'hôtellerie et le tertiaire.",
 "Professional refrigeration and air conditioning is one of the historical core activities of NANDA SÁ ENERGIA PRO. We design, install and maintain equipment for the food industry, fisheries, healthcare, logistics, hospitality and the tertiary sector.",
 "A refrigeração e a climatização profissional são um dos ofícios históricos centrais da NANDA SÁ ENERGIA PRO. Concebemos, instalamos e mantemos equipamentos para a agro-indústria, a pesca, a saúde, a logística, a hotelaria e o terciário."
 )}</p>
 <p>{tt(
 "Chaque projet démarre par une étude sérieuse : bilan thermique, choix des composants, cheminement des réseaux, contraintes climatiques et énergétiques. Le résultat est une installation calibrée pour durer et facile à entretenir.",
 "Every project starts with a serious study: thermal balance, component selection, piping layout, climatic and energy constraints. The result is an installation built to last and easy to maintain.",
 "Cada projecto começa por um estudo sério: balanço térmico, escolha dos componentes, encaminhamento das redes, restrições climáticas e energéticas. O resultado é uma instalação calibrada para durar e fácil de manter."
 )}</p>
 <p>{tt(
 "Nos équipes travaillent au Sénégal et en Guinée-Bissau, avec une chaîne d'approvisionnement fiable et des techniciens formés à la manipulation des fluides frigorigènes et aux règles de sécurité en vigueur.",
 "Our teams work in Senegal and Guinea-Bissau, with a reliable supply chain and technicians trained in refrigerant handling and current safety rules.",
 "As nossas equipas trabalham no Senegal e na Guiné-Bissau, com uma cadeia de abastecimento fiável e técnicos formados na manipulação de fluidos frigorígenos e nas regras de segurança em vigor."
 )}</p>
 </FadeUp>
 </div>
 </Section>

 {/* SERVICES */}
 <Section className="bg-nanda-bgAlt" id="services">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Nos services", "Our services", "Os nossos serviços")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Six pôles techniques.", "Six technical divisions.", "Seis pólos técnicos.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {SERVICES.map((s) => (
 <StaggerItem key={s.t} className="bg-white p-8 md:p-10">
 <s.i size={28} strokeWidth={1.2} className="text-nanda-terracotta mb-5" />
 <div className="font-serif text-2xl text-nanda-ink leading-tight">{s.t}</div>
 <p className="mt-4 text-[15px] text-nanda-inkSoft leading-relaxed">{s.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* SECTEURS SERVIS */}
 <Section id="secteurs">
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("Secteurs que nous servons", "Sectors we serve", "Sectores que servimos")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Six univers professionnels.", "Six professional universes.", "Seis universos profissionais.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {SECTEURS.map((s) => (
 <StaggerItem key={s.t} className="bg-white p-8 md:p-10">
 <s.i size={24} strokeWidth={1.2} className="text-nanda-ink mb-4" />
 <div className="font-serif text-xl md:text-2xl text-nanda-ink leading-tight">{s.t}</div>
 <p className="mt-3 text-sm text-nanda-inkSoft leading-relaxed">{s.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* RÉALISATIONS */}
 <Section id="realisations" className="bg-nanda-terracotta text-white">
 <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
 <FadeUp className="md:col-span-5">
 <div className="label-overline text-white/60 mb-4">{tt("Références techniques", "Technical references", "Referências técnicas")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05]">
 {tt("Nos réalisations et références techniques.", "Our projects and technical references.", "As nossas realizações e referências técnicas.")}
 </h2>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 text-white/75 text-[16px] leading-relaxed space-y-5">
 <p>{tt(
 "Quelques images issues de nos chantiers en Guinée-Bissau : réception de matériel, installations en chambre froide, groupes extérieurs, interventions techniques et réseaux frigorifiques.",
 "A few images from our projects in Guinea-Bissau: material reception, cold room installations, outdoor units, technical interventions and refrigeration piping networks.",
 "Algumas imagens das nossas obras na Guiné-Bissau: recepção de material, instalações em câmara fria, grupos exteriores, intervenções técnicas e redes frigoríficas."
 )}</p>
 <p>{tt(
 "Cliquez pour agrandir. Aucune donnée client, adresse ni puissance non confirmée n'est publiée : nous protégeons la confidentialité de nos projets.",
 "Click to enlarge. No client data, addresses or unconfirmed capacities are published: we protect the confidentiality of our projects.",
 "Clique para ampliar. Nenhum dado de cliente, endereço ou potência não confirmada é publicado: protegemos a confidencialidade dos nossos projectos."
 )}</p>
 </FadeUp>
 </div>
 <div className="text-nanda-ink">
 <RealisationsGallery />
 </div>
 </Section>

 {/* MÉTHODE */}
 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Notre méthode", "Our method", "O nosso método")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Six étapes, une exécution rigoureuse.", "Six steps, one rigorous execution.", "Seis etapas, uma execução rigorosa.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {METHODE.map((m) => (
 <StaggerItem key={m.n} className="bg-white p-8 md:p-10">
 <div className="font-serif text-nanda-terracotta text-4xl md:text-5xl mb-4">{m.n}</div>
 <div className="font-serif text-xl md:text-2xl text-nanda-ink leading-tight">{m.t}</div>
 <p className="mt-3 text-sm text-nanda-inkSoft leading-relaxed">{m.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* POURQUOI NOUS */}
 <Section>
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("Pourquoi NANDA SÁ ENERGIA PRO", "Why NANDA SÁ ENERGIA PRO", "Porquê a NANDA SÁ ENERGIA PRO")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Quatre atouts sur la durée.", "Four lasting strengths.", "Quatro vantagens no longo prazo.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
 {ATOUTS.map((a) => (
 <div key={a.t} className="flex flex-col gap-3">
 <a.i size={24} strokeWidth={1.2} className="text-nanda-terracotta" />
 <div className="font-serif text-xl text-nanda-ink">{a.t}</div>
 <p className="text-sm text-nanda-inkSoft leading-relaxed">{a.d}</p>
 </div>
 ))}
 </div>
 </Section>

 {/* CTA */}
 <Section className="bg-nanda-terracotta text-white">
 <FadeUp className="max-w-4xl mx-auto text-center">
 <Overline><span className="text-[#F6E7D7]">{tt("Nous consulter", "Get in touch", "Consulte-nos")}</span></Overline>
 <h3 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.08] mt-4">
 {tt("Un projet de chambre froide,", "A cold room project,", "Um projecto de câmara fria,")}<br />
 <span className="text-[#F6E7D7] italic">{tt("de climatisation ou de maintenance ?", "air conditioning or maintenance?", "de climatização ou de manutenção?")}</span>
 </h3>
 <p className="mt-8 text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
 {tt(
 "Décrivez votre besoin. Nous revenons vers vous avec une première évaluation technique et une proposition adaptée.",
 "Describe your requirement. We will come back to you with an initial technical assessment and a tailored proposal.",
 "Descreva a sua necessidade. Voltaremos com uma primeira avaliação técnica e uma proposta adaptada."
 )}
 </p>
 <div className="mt-12 flex flex-wrap gap-4 justify-center">
 <Link to="/devis?category=refrigeration-industrielle" className="inline-flex items-center gap-3 bg-white text-nanda-ink px-10 py-5 label-overline hover:bg-nanda-sand transition-colors duration-500" data-testid="refri-cta-final">
 {tt("Demander un devis", "Request a quote", "Solicitar orçamento")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <Link to="/contact" className="inline-flex items-center gap-3 border border-white/50 text-white px-10 py-5 label-overline hover:bg-nanda-sand transition-colors duration-500" data-testid="refri-cta-contact">
 {tt("Nous contacter", "Contact us", "Contacte-nos")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </div>
 </FadeUp>
 </Section>
 </div>
 );
}
